import { useState } from 'react';
import DataGridLayout from '../../components/layout/DataGridLayout';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Mail, Phone, Trash2, User, BookOpen, Eye, Edit, Download, Search } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import VerificationToggle from '../../components/ui/VerificationToggle';
import { EnquiryRequest } from '../../types/enquiry';
import EnquiryItemDialog from '../../components/enquiry/EnquiryItemDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';

const MOCK_SYLLABUS_REQUESTS: EnquiryRequest[] = [
  {
    id: '1',
    emailId: 'ravi@example.com',
    mobileNo: '9876543210',
    fullName: 'Ravi Kumar',
    course: 'Python Full Stack',
    isVerified: true
  },
  {
    id: '2',
    emailId: 'priya@example.com',
    mobileNo: '8765432109',
    fullName: 'Priya Sharma',
    course: 'Data Science',
    isVerified: false
  },
  {
    id: '3',
    emailId: 'ajay@example.com',
    mobileNo: '7654321098',
    fullName: 'Ajay Singh',
    course: 'UI/UX Design',
    isVerified: true
  },
  {
    id: '4',
    emailId: 'divya@example.com',
    mobileNo: '6543210987',
    fullName: 'Divya Patel',
    course: 'Web Development',
    isVerified: true
  },
  {
    id: '5',
    emailId: 'rahul@example.com',
    mobileNo: '9876543211',
    fullName: 'Rahul Gupta',
    course: 'DevOps Engineering',
    isVerified: false
  },
  {
    id: '6',
    emailId: 'neha@example.com',
    mobileNo: '8765432100',
    fullName: 'Neha Verma',
    course: 'Python AI and ML',
    isVerified: true
  }
];

const SyllabusRequests = () => {
  const [requests, setRequests] = useState<EnquiryRequest[]>(MOCK_SYLLABUS_REQUESTS);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<EnquiryRequest | null>(null);
  const [dialogMode, setDialogMode] = useState<'view' | 'edit' | 'create'>('view');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState<string | null>(null);
  const { toast } = useToast();

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleRefresh = () => {
    toast({
      title: "Refreshed",
      description: "Syllabus request data has been refreshed successfully.",
    });
  };

  const handleExport = () => {
    toast({
      title: "Export Started",
      description: "Your syllabus request data export has been initiated.",
    });
  };

  const handleAddNew = () => {
    setSelectedRequest(null);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const handleView = (request: EnquiryRequest) => {
    setSelectedRequest(request);
    setDialogMode('view');
    setDialogOpen(true);
  };

  const handleEdit = (request: EnquiryRequest) => {
    setSelectedRequest(request);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    setRequestToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (requestToDelete) {
      setRequests(prevRequests => prevRequests.filter(request => request.id !== requestToDelete));
      
      toast({
        title: "Entry Deleted",
        description: "The syllabus request has been removed successfully.",
      });
      setDeleteDialogOpen(false);
      setRequestToDelete(null);
    }
  };

  const handleToggleVerification = (id: string) => {
    setRequests(prevRequests => 
      prevRequests.map(request => 
        request.id === id 
          ? { ...request, isVerified: !request.isVerified } 
          : request
      )
    );

    toast({
      title: "Verification Status Updated",
      description: "The verification status has been updated successfully.",
    });
  };

  const handleSave = (updatedRequest: EnquiryRequest) => {
    if (dialogMode === 'edit') {
      setRequests(prevRequests => 
        prevRequests.map(request => request.id === updatedRequest.id ? updatedRequest : request)
      );
    } else if (dialogMode === 'create') {
      setRequests(prevRequests => [updatedRequest, ...prevRequests]);
    }
  };

  const handleSendSyllabus = (request: EnquiryRequest) => {
    toast({
      title: "Syllabus Sent",
      description: `The syllabus for ${request.course} has been sent to ${request.fullName}.`,
    });
  };

  const filteredRequests = requests.filter(request => 
    request.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    request.emailId.toLowerCase().includes(searchQuery.toLowerCase()) ||
    request.mobileNo.includes(searchQuery) ||
    (request.course && request.course.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <>
      <DataGridLayout
        title="Syllabus Requests"
        subtitle="Manage course syllabus requests and their verification status"
        searchPlaceholder="Search by name, email, phone or course..."
        onSearch={handleSearch}
        onRefresh={handleRefresh}
        onExport={handleExport}
        onAdd={handleAddNew}
      >
        <div className="rounded-md border">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="w-[250px]">Student Details</TableHead>
                <TableHead>Contact Info</TableHead>
                <TableHead>Course</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    No syllabus requests found
                  </TableCell>
                </TableRow>
              ) : (
                filteredRequests.map((request) => (
                  <TableRow key={request.id} className="hover:bg-slate-50">
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-[#0071bd]" />
                        <div>
                          <div>{request.fullName}</div>
                          <Badge variant="outline" className={request.isVerified ? 'bg-green-100 text-green-800 border-green-200' : 'bg-amber-100 text-amber-800 border-amber-200'}>
                            {request.isVerified ? 'Verified' : 'Pending'}
                          </Badge>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Mail size={14} className="text-blue-500" />
                          <span className="text-sm">{request.emailId}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone size={14} className="text-green-500" />
                          <span className="text-sm">{request.mobileNo}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <BookOpen size={16} className="text-amber-500" />
                        <span>{request.course}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <VerificationToggle 
                        isVerified={request.isVerified} 
                        onToggle={() => handleToggleVerification(request.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleView(request)}
                          className="text-[#0071bd] hover:text-[#0071bd] hover:bg-[#e6f4fb]"
                        >
                          <Eye size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(request)}
                          className="text-amber-500 hover:text-amber-600 hover:bg-amber-50"
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSendSyllabus(request)}
                          className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Download size={14} className="mr-1" />
                          Send
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(request.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </DataGridLayout>

      <EnquiryItemDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        item={selectedRequest}
        onSave={handleSave}
        mode={dialogMode}
        type="syllabus"
      />

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-red-500">Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this syllabus request? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SyllabusRequests;
